import { useQueryClient } from '@tanstack/react-query';

import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from '@repo/ui/menu';

import { canvasKeys } from '../api/canvas-captures';
import { CheckIcon, PeopleIcon } from '../lib/icons';
import { useAccounts } from './use-accounts';

/**
 * Personal / team switcher, dashboard-only for now.
 *
 * Renders nothing for an account with no teams — a switcher with one option
 * is a control that means nothing, and most accounts are exactly that today.
 * Every project/session/capture id is derived from the active scope
 * (`appIdFor`/`scopeKeyOf` on the API side), so switching has to invalidate
 * everything under the canvas root rather than one query — there is no
 * narrower key that would catch it.
 *
 * Not on /billing: nothing that page reads is scoped (see `WorkspacePicker`).
 */
export function AccountSwitcher() {
  const { loading, selectedTeamId, selectTeam, teams } = useAccounts();
  const queryClient = useQueryClient();

  if (loading || teams.length === 0) return null;

  const activeLabel =
    teams.find((team) => team.id === selectedTeamId)?.name ?? 'Personal';

  const choose = (teamId: string | null) => {
    if (teamId === selectedTeamId) return;
    selectTeam(teamId);
    void queryClient.invalidateQueries({ queryKey: canvasKeys.all });
  };

  return (
    <Menu>
      <MenuTrigger className="flex h-9 items-center gap-2 rounded-pill border border-hairline bg-surface px-4 text-ui font-semibold text-text-primary shadow-soft transition hover:bg-warm-alt">
        <PeopleIcon className="size-4 text-text-muted" />
        {activeLabel}
      </MenuTrigger>

      <MenuContent className="rounded-card border-hairline bg-surface shadow-soft">
        <MenuLabel className="text-text-secondary">Switch to</MenuLabel>

        <MenuItem
          className="cursor-pointer justify-between text-text-primary data-highlighted:bg-surface-hover"
          onClick={() => {
            choose(null);
          }}
        >
          Personal
          {selectedTeamId === null ? (
            <CheckIcon className="size-4 text-text-primary" />
          ) : null}
        </MenuItem>

        <MenuSeparator className="bg-hairline" />

        {teams.map((team) => (
          <MenuItem
            className="cursor-pointer justify-between text-text-primary data-highlighted:bg-surface-hover"
            key={team.id}
            onClick={() => {
              choose(team.id);
            }}
          >
            {team.name}
            {team.id === selectedTeamId ? (
              <CheckIcon className="size-4 text-text-primary" />
            ) : null}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}
